import { router, usePage } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { flash as flashToast } from "@/lib/toast"
import type { FlashData } from "@/types"

type Translator = ReturnType<typeof useTranslation>["t"]

function showFlash(flash: FlashData, t: Translator) {
  // Rails-builtin keys.
  if (flash.alert) toast.error(flash.alert)
  if (flash.notice) toast(flash.notice)
  // OKS-13 / OKS-14 semantic keys (per controller service-result
  // discriminants — `success` for `:invited` / `:replaced` /
  // `:brand_created`; `error` for `:already_member` / `:invalid` /
  // `:error`). Both pairs honored so controllers keep their semantic
  // names without a renormalize step.
  if (flash.error) {
    // OKS-19 P4 fix #2 — `flash.error` may be a plain string
    // (legacy + most current error branches) OR a structured
    // `FlashErrorObject` carrying `{code, message}` (the bulk
    // `cap_exceeded` branch per BE Lead's `11f3e5c`). Extract the
    // human-readable message for toast.error in both cases. Pages
    // that need to discriminate by `code` (e.g. Index.tsx → render
    // the inline cap-exceeded alert above the bulk bar) read the
    // object form directly — useFlash only handles the toast.
    if (typeof flash.error === "string") {
      toast.error(flash.error)
    } else {
      toast.error(flash.error.message)
    }
  }
  // OKS-30 — BE-emitted i18n key. Compose the toast via `t(...)` so
  // the keys stay discoverable to the inertia_i18n health checker and
  // locale switches don't need a server round-trip.
  //
  // OKS-494 — optional `error_vars` are merged into the interpolation
  // bag so keys that name live values (e.g. `out_of_stock` →
  // `%{remaining}` / `%{attempted}`) render correctly. The legacy
  // `{limit: 5000}` default stays for `too_large`; `error_vars` (when
  // absent) spreads nothing, so every existing `error_key` consumer is
  // byte-for-byte unchanged. The consume-once guard keys off the flash
  // CONTENT signature, which already includes `error_vars`, so adding
  // them does not perturb the OKS-491 dedup.
  if (flash.error_key) {
    toast.error(t(flash.error_key, { limit: 5000, ...flash.error_vars }))
  }
  // OKS-485 — success-side sibling of `error_key`. Composes the success
  // toast from a BE-emitted i18n key so the copy stays FE-localized.
  //
  // OKS-553 — optional `success_vars` are merged into the interpolation
  // bag (mirror of `error_vars`) so success keys that name live values
  // render them — e.g. `missions.audience.flash.now_all_crew_assigned`'s
  // `%{count}` on the All-crew toggle backfill. When absent, spreads
  // nothing, so every existing `success_key` consumer is byte-for-byte
  // unchanged.
  if (flash.success_key) {
    toast.success(t(flash.success_key, { ...flash.success_vars }))
  }
  // OKS-553 — amber sibling of `error_key`/`success_key`. Composes an
  // FE-localized warning toast from a BE-emitted i18n key + optional
  // `warning_vars`. Distinct from the OKS-184 `flash.warning` (BE-resolved
  // plain string) — this path stays FE-localized so the audience-toggle
  // guard copy lives in the frontend bundle with its success sibling. The
  // `{ limit: 5000 }` default mirrors the `error_key` path so
  // `missions.audience.flash.too_large`'s `%{limit}` renders even when the
  // controller omits it; `warning_vars` overrides/extends it.
  if (flash.warning_key) {
    toast.warning(t(flash.warning_key, { limit: 5000, ...flash.warning_vars }))
  }
  // OKS-30 — Structured assignment-result. Composes the human message
  // from the three counts via i18n pluralization (FR plural rules).
  if (flash.assignment_result) {
    const { assigned_count, skipped_already_count, skipped_status_count } =
      flash.assignment_result
    if (assigned_count === 0 && skipped_already_count > 0) {
      toast.success(t("missions.assign.flash.nothing_new"))
    } else if (skipped_already_count > 0 || skipped_status_count > 0) {
      toast.success(
        t("missions.assign.flash.assigned_with_skips", {
          count: assigned_count,
          skipped: skipped_already_count + skipped_status_count,
        }),
      )
    } else if (assigned_count > 0) {
      toast.success(
        t("missions.assign.flash.assigned", { count: assigned_count }),
      )
    }
  }
  if (flash.success) {
    // OKS-19 — `flash.success` may be a plain string (legacy
    // OKS-13/14/18 pattern) OR a structured `FlashSuccessObject`
    // carrying `{message?, bulk_result?}`. We toast the message form
    // here; bulk-only payloads (no `message`, only `bulk_result`) are
    // consumed by `pages/Brand/Candidates/Index.tsx` which owns the
    // 6s skipped-rows toast + the router-coupled `View skipped` link.
    // Without this discrimination, sonner would render `[object Object]`
    // for every bulk redirect (regression risk per memory
    // `feedback_useflash_dropped_silently`).
    if (typeof flash.success === "string") {
      toast.success(flash.success)
    } else if (flash.success.message) {
      toast.success(flash.success.message)
    }
    // bulk_result-only success → intentional no-op here.
  }
  // OKS-184 — Plain-string warning toast (amber chrome, TriangleAlertIcon
  // already wired in `components/ui/sonner.tsx`). First consumer is
  // `MissionsController` on the draft-save + past-deadline branch — the
  // BE composes `flash: {success: ..., warning: ...}` so this branch
  // fires AFTER `flash.success` above; sonner stacks the two toasts
  // (success first, warning second) per UX Spec §4 stack order. Plain
  // string only — no structured-object variant (PRD §3 lock).
  if (flash.warning) {
    toast.warning(flash.warning)
  }
  // OKS-113 — Structured `toast` flash carrying the i18n KEY alongside the
  // BE-resolved `message`. Delegated to the shared `flash()` helper, which
  // renders the message inside a `<span data-toast-key={key}>` so Playwright
  // can assert `[data-toast-key="<key>"]` regardless of active locale. The
  // message is BE-resolved (admin.*.flash.* copy lives only in the backend
  // bundle), so the helper does NOT call `t(key)` on this path. Admin-only
  // consumer this ticket; see `lib/toast.ts` + TRD OKS-113.
  if (flash.toast) {
    flashToast(flash.toast.key, {
      message: flash.toast.message,
      variant: flash.toast.variant,
    })
  }
}

/**
 * Subscribe to Inertia's `flash` payload and dispatch a sonner toast
 * per non-empty key. Mounted once by `PersistentLayout` so every
 * Inertia visit's flash surfaces exactly once (not per-page-mount).
 *
 * Path discipline: `usePage().flash` — Inertia surfaces flash at the
 * top of the Page object (see `@inertiajs/core` `Page<TProps>`
 * interface, `flash: FlashData`). An earlier "fix" attempt swapped
 * this to `usePage().props.flash` based on a wrong reading of the
 * gem's auto-share path; that broke the OKS-15 admin agency-attach
 * toasts which were working correctly. Top-level is the canon.
 */
export function useFlash() {
  const { flash } = usePage()
  const { t } = useTranslation()
  // OKS-491 — content-signature of the last flash we actually dispatched.
  // Survives re-renders (ref, not state) so the consume-once guard below
  // persists across Inertia partial reloads.
  const lastShownSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    // OKS-491 — consume-once guard. Inertia re-shares the SAME `flash`
    // payload on every `only:`-scoped partial reload (notably the OKS-406
    // `CreatorChannel` catch-up `router.reload({ only: CATCH_UP_PROPS })`
    // fired on connect/reconnect). `flash` lives at the root of the Page
    // object, so it rides along even though it isn't in the `only:` set, and
    // the client rebuilds it as a NEW object reference each reload — which
    // re-runs this effect (dep `[flash, t]`) and, pre-fix, re-dispatched the
    // toast on every reconnect (the "Connexion réussie" drip + multi-tab
    // bursts of OKS-491, empirically 1→3 within ~1s). Skip dispatch when the
    // flash CONTENT is byte-identical to the one we last showed; a genuinely
    // new payload (different content) still toasts. Scoped to the toast
    // lifecycle — not a flash-payload-lifecycle refactor (PRD OQ-2).
    const signature = JSON.stringify(flash ?? {})
    if (signature === lastShownSignatureRef.current) return

    // setTimeout + cleanup prevents double-firing in React StrictMode. The
    // signature is recorded INSIDE the callback (only once the toast actually
    // fires) so StrictMode's mount→cleanup→mount cycle — which clears the
    // first timer before it runs — still surfaces exactly one toast rather
    // than being suppressed by a prematurely-set ref.
    const timeout = setTimeout(() => {
      showFlash(flash, t)
      lastShownSignatureRef.current = signature
    }, 0)
    return () => clearTimeout(timeout)
  }, [flash, t])

  // OKS-523 — scope the OKS-491 consume-once guard to catch-up re-deliveries
  // ONLY. Reset the content-signature on a GENUINE full Inertia visit so a
  // legitimate repeat action whose success copy is byte-identical to the
  // previous one toasts AGAIN, while a reconnect / partial `only:` catch-up
  // re-delivery of an already-shown flash stays deduped (the OKS-491
  // guarantee).
  //
  // Event choice — `navigate`, NOT `success`/`finish` (verified against
  // @inertiajs/core@2.x): `Page.set()` gates `fireNavigateEvent()` behind
  // `if (!replace)`, and a same-URL response forces `replace = true`
  // (`isSameUrlWithoutHash`). The OKS-406 catch-up
  // `router.reload({ only: CATCH_UP_PROPS })` is a same-URL partial reload →
  // `replace = true` → `navigate` never fires → the guard is NOT reset → the
  // re-delivery stays suppressed (arbiter `login-toast-once-oks491` green).
  // `success`/`finish` fire on EVERY successful visit including partial
  // reloads, so they would reset the guard on the catch-up and re-toast.
  //
  // Registered once (empty deps — the hook mounts once in `PersistentLayout`)
  // and separate from the `[flash, t]` dispatch effect so the subscription
  // isn't torn down/re-added on every flash change. `router.on` returns its
  // unsubscribe fn, returned here for clean teardown. Resetting the ref to
  // `null` cannot re-fire the dispatch effect (its deps `[flash, t]` are
  // unchanged), so a genuine visit still toasts exactly once.
  useEffect(() => {
    return router.on("navigate", () => {
      lastShownSignatureRef.current = null
    })
  }, [])
}
